import { Component, OnInit, OnDestroy, NgZone } from "@angular/core";
import {
  Cliente,
  CodigoEtapa,
  EtapaAtual,
  Proposta,
  Resposta,
  StatusEtapa,
  StatusProposta,
  TipoColeta,
} from "src/app/models/coleta";
import { PropostaService } from "src/app/services/proposta.service";
import { SessionStorageService } from "src/app/services/session-storage.service";
import { delay, map, retry, catchError } from "rxjs/operators";
import { Subscription } from "rxjs";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { DialogComponent } from "src/app/components/dialog/dialog.component";
import { AppFlowService } from "src/app/services/app-flow.service";
import { ColetaService } from "src/app/services/coleta.service";
import { UrlParamsService } from "src/app/services/url-params.service";
import { ConfirmationDialogComponent } from "src/app/components/confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-waiting-page",
  templateUrl: "./waiting-page.component.html",
  styleUrls: ["./waiting-page.component.scss"],
})
export class WaitingPageComponent implements OnInit, OnDestroy {
  readonly INTERVALO_CONSULTAS = 6000;
  readonly LIMITE_TENTATIVAS_CONSULTA = -1;

  proposta: Proposta = {
    id: "",
    cliente: {
      documento: "",
      nome: "",
    },
    origemComercial: 0,
    etapaAtual: {
      id: 0,
      status: StatusEtapa.INICIADO,
      observacao: "",
      tipo: "",
      codigo: CodigoEtapa.NEUROTECH,
      nome: "",
      configuracao: 0,
      coleta: 0,
      exibeMarcador: false,
      tipoColeta: "",
    },
    produto: 0,
    status: StatusProposta.EM_ANDAMENTO,
    documentos: [],
    data: new Date(),
    usuario: 0,
    marcadores: [],
    fluxo: 0,
    reenviosSmsTac: 0,
  };

  // propostaId?: Proposta | null;

  exibirMotivoReprovacao = false;
  isRetomada = true;

  loader: string = "";
  waitingTitle: string = "";
  waitingMessages: string[] = [];
  waitingObservation: string = "";

  propostaAtualizadaSubscriptionRetry: Subscription | null = null;

  constructor(
    private sessionStorageService: SessionStorageService,
    private propostaService: PropostaService,
    private router: Router,
    private appFlowService: AppFlowService,
    private coletaService: ColetaService,
    public dialog: MatDialog,
    private urlParams: UrlParamsService,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone
  ) {}

  ngOnDestroy(): void {
    if (this.propostaAtualizadaSubscriptionRetry) {
      this.propostaAtualizadaSubscriptionRetry.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((params) => {
      const envConfig = this.sessionStorageService.getEnvConfig();
      this.loader = envConfig.waitingLoader || "default";
      this.waitingTitle = envConfig.waitingTitle || "";
      this.waitingMessages = envConfig.waitingMessages || [];

      this.waitingObservation = this.getRandomMessage();

      const propostaId = params.get("id");
      if (propostaId) {
        this.proposta.id = propostaId;
      } else {
        const appState = this.appFlowService.getAppState();
        if (!appState.proposta) return;
        this.proposta = appState.proposta;
      }

      if (this.proposta.retomada) {
        this.isRetomada = true;
        this.openConfirmationDialog();

        return;
      }

      this.iniciarBusca(this.proposta);
    });
  }

  private iniciarBusca(proposta: Proposta): void {
    this.propostaAtualizadaSubscriptionRetry = this.propostaService
      .buscar(proposta.id)
      .pipe(
        delay(this.INTERVALO_CONSULTAS),
        map((respostaProposta: Proposta) => {
          if (this.deveAguardarProcessamentoProposta(respostaProposta)) {
            throw null;
          }

          return respostaProposta;
        }),
        retry(this.LIMITE_TENTATIVAS_CONSULTA)
      )
      .subscribe((response) => {

        this.ngZone.run(() => {

          this.tratarAtualizacaoStatus(response);
          });
      });
  }

  getRandomMessage(): string {
    if (!this.waitingMessages) return "";
    const randomPosition: number = Math.round(
      Math.random() * this.waitingMessages.length
    );

    const randomMessage: string = this.waitingMessages[randomPosition];

    return randomMessage;
  }

  private deveAguardarProcessamentoProposta(proposta: Proposta): boolean {
    return (
      StatusProposta.EM_ANDAMENTO === proposta.status &&
      StatusEtapa.PREENCHIMENTO !== proposta.etapaAtual.status &&
      CodigoEtapa.MESA !== proposta.etapaAtual.codigo
    );
  }

  openConfirmationDialog(): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Olá! ",
        message:
          "Identificamos que já existe uma proposta em andamento para este CPF. Deseja continuar?",
        confirmed: false,
      },
      maxWidth: "350px",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !result.confirmed) {
        this.appFlowService.clearAppState();
        this.router.navigate(["/"]);
      }
      this.iniciarBusca(this.proposta);
    });
  }

  private tratarAtualizacaoStatus(proposta: Proposta): void {
    const envConfig = this.sessionStorageService.getEnvConfig();

    let mensagem = "";

    switch (proposta.status) {
      case StatusProposta.REPROVADO:
        mensagem =
          envConfig.mensagemReprovacao ||
          proposta.etapaAtual.observacao ||
          "A sua proposta foi negada.";

        this.dialog.open(DialogComponent, {
          width: "250px",
          data: { title: "Oooops!", message: mensagem },
        });

        this.appFlowService.clearAppState();
        this.urlParams.colocarParametrosURL();
        break;
      case StatusProposta.EXPIRADO:
        mensagem =
          envConfig.mensagemPropostaExpirada ||
          "Não foi possível continuar com sua solicitação. Tente novamente para iniciar uma nova verificação.";

        this.dialog.open(DialogComponent, {
          width: "250px",
          data: { title: "Oooops!", message: mensagem },
        });

        this.appFlowService.clearAppState();
        this.urlParams.colocarParametrosURL();
        break;
      case StatusProposta.INCONSISTENTE:
        mensagem =
          envConfig.mensagemInconsistencia ||
          "Algum problema técnico ocorreu. Favor contatar o suporte.";

        this.dialog.open(DialogComponent, {
          width: "250px",
          data: { title: "Oooops!", message: mensagem },
        });

        this.appFlowService.clearAppState();
        this.urlParams.colocarParametrosURL();
        break;
      case StatusProposta.EM_ANDAMENTO:
        switch (proposta.etapaAtual.status) {
          case StatusEtapa.PREENCHIMENTO:
            this.coletaService
              .getRespondidos(proposta.etapaAtual.coleta, proposta.id, [
                "origem=WHITELABEL",
                `etapa=${proposta.etapaAtual.configuracao}`,
                `produto=${proposta.produto}`,
              ])
              .then((response) => {
                if (proposta.etapaAtual.tipoColeta === TipoColeta.CORRECAO) {
                  this.propostaService
                    .buscarEtapaAnterior(proposta.etapaAtual.id)
                    .subscribe((etapaAnterior) => {
                      this.dialog.open(DialogComponent, {
                        width: "250px",
                        data: {
                          title: "Atenção!",
                          message: etapaAnterior.observacao,
                        },
                      });
                    });
                }

                this.appFlowService.clearAppState();

                this.appFlowService.startApp(response).then((newAppState) => {
                  newAppState.proposta = proposta;
                  this.sessionStorageService.saveAppState(newAppState);
                });
                this.router.navigate(["/preenchimento"]);
              });
            break;
          case StatusEtapa.EM_ANALISE:
            this.appFlowService.clearAppState();
            this.router.navigate(["/enviado"]);
            break;
        }
        break;
      case StatusProposta.APROVADO:
        this.appFlowService.clearAppState();
        this.router.navigate(["/aprovado"]);
        break;
    }
  }
}
